import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  Users, Plus, Edit, Trash2, Save, X, Search, Eye, EyeOff,
  Shield, Mail, Phone, Building2, CheckCircle, AlertTriangle,
  UserCheck, Lock, Unlock, RefreshCw, Key
} from 'lucide-react';
import { rolePermissions } from '../../data/mockData';
import { getRoleDisplayName } from '../../utils/helpers';
import { useConfirm } from '../Common/ConfirmDialog';

const USERS_KEY = 'medfast_users';
const ROLES = Object.keys(rolePermissions);

const getUsers = () => {
  try { return JSON.parse(localStorage.getItem(USERS_KEY) || '[]'); }
  catch { return []; }
};

const saveUsers = (users) => {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
};

const hashPassword = (password) => {
  try {
    const CryptoJS = require('crypto-js');
    const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
    const hash = CryptoJS.SHA256(password + salt).toString();
    return { hash, salt };
  } catch {
    return { hash: password, salt: '' };
  }
};

const EMPTY_FORM = {
  name: '',
  username: '',
  password: '',
  confirmPassword: '',
  role: 'nurse',
  email: '',
  phone: '',
  title: '',
  facilities: [],
  status: 'active'
};

const UserManagement = () => {
  const { state } = useAppContext();
  const confirm = useConfirm();
  const { currentUser } = state;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadUsers = useCallback(() => {
    setLoading(true);
    try {
      const data = getUsers();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users');
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = !searchQuery ||
        u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = filterRole === 'all' || u.role === filterRole;
      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, filterRole]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.username.trim()) return 'Username is required';
    if (!formData.email.trim()) return 'Email is required';

    if (!editingId) {
      if (!formData.password) return 'Password is required';
      if (formData.password.length < 10) return 'Password must be at least 10 characters';
      if (!/[A-Z]/.test(formData.password)) return 'Password needs an uppercase letter';
      if (!/[a-z]/.test(formData.password)) return 'Password needs a lowercase letter';
      if (!/[0-9]/.test(formData.password)) return 'Password needs a digit';
      if (!/[^A-Za-z0-9]/.test(formData.password)) return 'Password needs a special character';
      if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    }

    const existing = users.find(u =>
      u.username === formData.username.trim().toLowerCase() && u.id !== editingId
    );
    if (existing) return 'Username already taken';

    return null;
  };

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setSaving(true);
    setError(null);

    try {
      const allUsers = getUsers();

      if (editingId) {
        const idx = allUsers.findIndex(u => u.id === editingId);
        if (idx === -1) throw new Error('User not found');
        allUsers[idx] = {
          ...allUsers[idx],
          name: formData.name.trim(),
          username: formData.username.trim().toLowerCase(),
          role: formData.role,
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          title: formData.title.trim(),
          facilities: formData.facilities,
          status: formData.status,
          updatedAt: new Date().toISOString()
        };
        saveUsers(allUsers);
        flash('User updated successfully');
      } else {
        const { hash, salt } = hashPassword(formData.password);
        const newUser = {
          id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          name: formData.name.trim(),
          username: formData.username.trim().toLowerCase(),
          role: formData.role,
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          title: formData.title.trim(),
          facilities: formData.facilities.length ? formData.facilities : ['all'],
          permissions: rolePermissions[formData.role]?.permissions || [],
          status: formData.status || 'active',
          passwordHash: hash,
          salt,
          passwordHistory: [],
          passwordChangedAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
        };
        allUsers.push(newUser);
        saveUsers(allUsers);
        flash('User created successfully');
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      loadUsers();
    } catch (err) {
      setError(err.message);
      console.error('Save user error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      name: user.name || '',
      username: user.username || '',
      password: '',
      confirmPassword: '',
      role: user.role || 'nurse',
      email: user.email || '',
      phone: user.phone || '',
      title: user.title || '',
      facilities: user.facilities || [],
      status: user.status || 'active'
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser?.id) {
      setError('You cannot delete your own account');
      return;
    }
    if (!await confirm({ title: 'Delete User', message: 'Are you sure you want to delete this user? This action cannot be undone.', confirmLabel: 'Delete', variant: 'danger' })) return;

    const allUsers = getUsers().filter(u => u.id !== userId);
    saveUsers(allUsers);
    flash('User deleted');
    loadUsers();
  };

  const handleResetPassword = () => {
    if (!newPassword || newPassword.length < 10) {
      setError('New password must be at least 10 characters');
      return;
    }
    const allUsers = getUsers();
    const idx = allUsers.findIndex(u => u.id === resetPasswordId);
    if (idx === -1) return;

    const { hash, salt } = hashPassword(newPassword);
    allUsers[idx].passwordHash = hash;
    allUsers[idx].salt = salt;
    allUsers[idx].passwordChangedAt = new Date().toISOString();
    saveUsers(allUsers);
    setResetPasswordId(null);
    setNewPassword('');
    flash('Password reset successfully');
    loadUsers();
  };

  const handleToggleStatus = (userId) => {
    if (userId === currentUser?.id) {
      setError('You cannot disable your own account');
      return;
    }
    const allUsers = getUsers();
    const idx = allUsers.findIndex(u => u.id === userId);
    if (idx === -1) return;
    allUsers[idx].status = allUsers[idx].status === 'active' ? 'disabled' : 'active';
    saveUsers(allUsers);
    loadUsers();
  };

  const getRoleBadgeColor = (role) => {
    const map = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-purple-100 text-purple-800',
      doctor: 'bg-blue-100 text-blue-800',
      nurse: 'bg-green-100 text-green-800',
      pharmacist: 'bg-teal-100 text-teal-800',
      care_worker: 'bg-amber-100 text-amber-800',
      family: 'bg-pink-100 text-pink-800'
    };
    return map[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              User Management
              <Users className="w-7 h-7 text-indigo-300" />
            </h2>
            <p className="text-sm mt-1 opacity-90">
              Create, manage, and configure user accounts and access
            </p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setFormData({ ...EMPTY_FORM }); setError(null); }}
            className="bg-white text-indigo-600 px-5 py-2.5 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-5 border border-indigo-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-200 rounded-lg"><Users className="w-5 h-5 text-indigo-700" /></div>
            <div>
              <h3 className="text-2xl font-bold text-indigo-900">{users.length}</h3>
              <p className="text-xs text-indigo-600">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-5 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-200 rounded-lg"><UserCheck className="w-5 h-5 text-green-700" /></div>
            <div>
              <h3 className="text-2xl font-bold text-green-900">{users.filter(u => u.status !== 'disabled').length}</h3>
              <p className="text-xs text-green-600">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border border-purple-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-200 rounded-lg"><Shield className="w-5 h-5 text-purple-700" /></div>
            <div>
              <h3 className="text-2xl font-bold text-purple-900">{users.filter(u => u.role === 'admin').length}</h3>
              <p className="text-xs text-purple-600">Admins</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-5 border border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-200 rounded-lg"><Lock className="w-5 h-5 text-amber-700" /></div>
            <div>
              <h3 className="text-2xl font-bold text-amber-900">{users.filter(u => u.status === 'disabled').length}</h3>
              <p className="text-xs text-amber-600">Disabled</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">{successMsg}</p>
        </div>
      )}

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingId ? 'Edit User' : 'Create New User'}
            </h3>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="p-2 hover:bg-gray-200 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Identity */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-600" /> Identity
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" value={formData.title} onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Registered Nurse" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                  <input type="text" value={formData.username} onChange={(e) => handleChange('username', e.target.value)} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="jsmith" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select value={formData.role} onChange={(e) => handleChange('role', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    {ROLES.map(r => <option key={r} value={r}>{getRoleDisplayName(r)}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4 text-indigo-600" /> Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="jsmith@medfast.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="+61 400 123 456" />
                </div>
              </div>
            </div>

            {/* Password — only on create */}
            {!editingId && (
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Key className="w-4 h-4 text-indigo-600" /> Password
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => handleChange('password', e.target.value)} required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-10" placeholder="Min 10 chars, upper/lower/digit/special" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-8 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                    <input type="password" value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Re-enter password" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Minimum 10 characters with uppercase, lowercase, digit, and special character
                </p>
              </div>
            )}

            {/* Status */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-indigo-600" /> Account Status
              </h4>
              <select value={formData.status} onChange={(e) => handleChange('status', e.target.value)}
                className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700 font-medium flex items-center gap-2 disabled:opacity-50">
                {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> {editingId ? 'Update User' : 'Create User'}</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Password Reset Modal */}
      {resetPasswordId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Reset Password</h3>
              <button onClick={() => { setResetPasswordId(null); setNewPassword(''); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Resetting password for: <strong>{users.find(u => u.id === resetPasswordId)?.name}</strong>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Min 10 characters" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setResetPasswordId(null); setNewPassword(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
                <button type="button" onClick={handleResetPassword}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-medium flex items-center justify-center gap-2">
                  <Key className="w-4 h-4" /> Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, username, or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
          </div>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            <option value="all">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{getRoleDisplayName(r)}</option>)}
          </select>
        </div>
      </div>

      {/* User Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-lg font-medium">No users found</p>
            <p className="text-sm">Create one using the button above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs font-bold uppercase text-gray-600">
                <tr>
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-indigo-700">
                            {(user.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">@{user.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-600">{user.email || '—'}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        user.status === 'disabled' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {user.status === 'disabled' ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-xs">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(user)} title="Edit user"
                          className="p-2 hover:bg-indigo-50 rounded-lg transition-colors">
                          <Edit className="w-4 h-4 text-indigo-600" />
                        </button>
                        <button onClick={() => setResetPasswordId(user.id)} title="Reset password"
                          className="p-2 hover:bg-amber-50 rounded-lg transition-colors">
                          <Key className="w-4 h-4 text-amber-600" />
                        </button>
                        <button onClick={() => handleToggleStatus(user.id)} title={user.status === 'disabled' ? 'Enable' : 'Disable'}
                          className="p-2 hover:bg-orange-50 rounded-lg transition-colors">
                          {user.status === 'disabled'
                            ? <Unlock className="w-4 h-4 text-green-600" />
                            : <Lock className="w-4 h-4 text-orange-600" />}
                        </button>
                        <button onClick={() => handleDelete(user.id)} title="Delete user"
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
